// apps/admin/src/features/billing/CreditMemoApprovalActionsCell.tsx
//
// Phase 8.2 Wave 3 Task 19 — Per-row Approve + Reject action cell for the
// Credit Memo Pending Approval Queue (CreditMemoApprovalsPage).
//
// vg-binding: CONTEXT:decisions=P8.D-496,P8.D-490,P8.D-495
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Per CONTEXT.md (Amendment #3 — new Pending Approval Queue page):
//   - **Approve** (admin only, `credit_memo:approve` perm): POST
//     /api/v1/admin/credit-memos/:id/approve → status ACTIVE.
//   - **Reject**: required reason field → POST /api/v1/admin/credit-memos/:id/reject
//     → status REJECTED. BE emits email kế toán notification
//     wording ("Your credit_memo for $X to merchant Y was rejected: {reason}").
//   - Audit log entry on every approve/reject.
//
// Per API-CONTRACTS § POST /admin/credit-memos/:id/reject:
//   - reason: z.string().min(20).max(1000).
//   - Response: { ok: true, data: { id, status:'REJECTED', rejected_by,
//                rejected_at, audit_log_id }, message }.
//   - Error codes: 401/403/404/409 NOT_PENDING_APPROVAL.
//
//  (Finance permission gating standard):
//   - Approve button is HIDDEN (not just disabled) for users without
//     `credit_memo:approve`. We delegate the perm check to the parent page
//     (props.canApprove) so a single perm lookup serves the whole table.
//
//  (audit-log pattern):
//   - BE writes the audit_log entry — FE does not POST anything separately.
//
// Test IDs (exact strings per task brief):
//   credit-memo-approve-btn-{memoId}
//   credit-memo-reject-btn-{memoId}
//   credit-memo-reject-modal
//   credit-memo-reject-reason-input

import { useState, type JSX } from 'react';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Constants — kept in sync with API-CONTRACTS reject schema (20-1000 chars).
// ---------------------------------------------------------------------------

const REJECT_REASON_MIN = 20;
const REJECT_REASON_MAX = 1000;

// ---------------------------------------------------------------------------
// Types — mirror API-CONTRACTS reject + approve response envelopes. Hand-
// rolled since shared/api/types.generated.ts has not yet shipped the Phase
// 8.2 credit-memo-approval slice (codegen lands at /vg:test).
// ---------------------------------------------------------------------------

interface ApiErrorEnvelope {
  ok?: false;
  success?: false;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
    field_errors?: Record<string, string[] | string>;
  };
}

// Loosely-typed openapi-fetch surface — matches the pattern used by sibling
// billing pages (CreditMemoListPage, RefundTaskQueuePage). The generated
// `paths` does not yet cover Phase 8.2 routes.
type LooseClient = {
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractApiMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idemp-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CreditMemoApprovalActionsCellProps {
  /** Credit memo id (Mongo ObjectId, 24-char hex). */
  memoId: string;
  /**
   * Optional short merchant label used in the reject modal subtitle so the
   * admin double-checks they're rejecting the right row.
   */
  merchantLabel?: string | null;
  /** Optional USD-cents amount also surfaced in the reject confirmation. */
  amountUsdCents?: number | null;
  /**
   * the Approve button is hidden (not just disabled) when
   * the current user lacks `credit_memo:approve`. The parent page does the
   * perm lookup once and forwards the boolean here.
   */
  canApprove: boolean;
  /**
   * Fired after a successful approve OR reject so the parent can refresh
   * the queue (refetch list + decrement pending count badge).
   */
  onResolved: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreditMemoApprovalActionsCell({
  memoId,
  merchantLabel,
  amountUsdCents,
  canApprove,
  onResolved,
}: CreditMemoApprovalActionsCellProps): JSX.Element {
  const { t } = useTranslation('finance');
  const { toast } = useToast();

  // ---- Approve state ----
  const [approving, setApproving] = useState(false);

  // ---- Reject modal state ----
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);

  const reasonTrimLen = rejectReason.trim().length;
  const reasonValid =
    reasonTrimLen >= REJECT_REASON_MIN && reasonTrimLen <= REJECT_REASON_MAX;

  function openReject(): void {
    setRejectReason('');
    setRejectError(null);
    setRejectOpen(true);
  }

  function closeReject(): void {
    if (rejecting) return;
    setRejectOpen(false);
    setRejectReason('');
    setRejectError(null);
  }

  async function handleApprove(): Promise<void> {
    if (!canApprove) {
      // Defensive — button should be hidden, but a programmatic invocation
      // (e.g. keyboard shortcut from a future enhancement) lands here.
      toast({
        title: t('credit_memo.toast.adminRequired'),
        description: t('credit_memo.actionsCell.noApprovePerm'),
        variant: 'warning',
      });
      return;
    }
    setApproving(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/credit-memos/${encodeURIComponent(memoId)}/approve`,
        {
          body: {},
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );
      if (error || !response.ok) {
        toast({
          title: t('credit_memo.toast.approveFailed'),
          description: extractApiMessage(error, t('credit_memo.toast.genericError')),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('credit_memo.toast.approved'),
        description: t('credit_memo.toast.approvedDesc'),
        variant: 'success',
      });
      onResolved();
    } catch (err) {
      toast({
        title: t('credit_memo.toast.networkError'),
        description:
          err instanceof Error && err.message
            ? err.message
            : t('credit_memo.toast.networkErrorDesc'),
        variant: 'error',
      });
    } finally {
      setApproving(false);
    }
  }

  async function handleRejectSubmit(): Promise<void> {
    if (!reasonValid) {
      setRejectError(
        t('credit_memo.actionsCell.reasonRequired', {
          min: REJECT_REASON_MIN,
          max: REJECT_REASON_MAX,
        }),
      );
      return;
    }
    setRejectError(null);
    setRejecting(true);
    try {
      const client = apiClient() as unknown as LooseClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/credit-memos/${encodeURIComponent(memoId)}/reject`,
        {
          body: { reason: rejectReason.trim() },
          headers: { 'Idempotency-Key': newIdempotencyKey() },
        },
      );
      if (error || !response.ok) {
        // 409 NOT_PENDING_APPROVAL → another admin already resolved this
        // memo; treat as race-loser and refresh the queue.
        const status = response.status;
        if (status === 409) {
          toast({
            title: t('credit_memo.actionsCell.alreadyResolvedTitle'),
            description: t('credit_memo.actionsCell.alreadyResolvedDesc'),
            variant: 'warning',
          });
          setRejectOpen(false);
          onResolved();
          return;
        }
        const msg = extractApiMessage(error, t('credit_memo.toast.genericError'));
        setRejectError(msg);
        toast({
          title: t('credit_memo.actionsCell.rejectFailed'),
          description: msg,
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('credit_memo.actionsCell.rejectedTitle'),
        description: t('credit_memo.actionsCell.rejectedDesc'),
        variant: 'success',
      });
      setRejectOpen(false);
      onResolved();
    } catch (err) {
      const msg =
        err instanceof Error && err.message
          ? err.message
          : t('credit_memo.toast.networkErrorDesc');
      setRejectError(msg);
      toast({
        title: t('credit_memo.toast.networkError'),
        description: msg,
        variant: 'error',
      });
    } finally {
      setRejecting(false);
    }
  }

  // ---- Render ----
  return (
    <>
      <div className="flex items-center justify-end gap-2">
        {canApprove ? (
          <button
            type="button"
            onClick={() => void handleApprove()}
            disabled={approving || rejecting}
            data-testid={`credit-memo-approve-btn-${memoId}`}
            className="inline-flex h-8 items-center rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            title={t('credit_memo.actionsCell.approveTooltip')}
          >
            {approving ? t('credit_memo.actions.approving') : t('credit_memo.approvals.approve')}
          </button>
        ) : null}
        <button
          type="button"
          onClick={openReject}
          disabled={approving || rejecting}
          data-testid={`credit-memo-reject-btn-${memoId}`}
          className="inline-flex h-8 items-center rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-2.5 text-xs font-medium text-rose-700 dark:text-rose-300 transition-colors hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50"
          title={t('credit_memo.actionsCell.rejectTooltip')}
        >
          {t('credit_memo.approvals.reject')}
        </button>
      </div>

      <Dialog
        open={rejectOpen}
        onOpenChange={(o) => {
          if (!o) closeReject();
        }}
        className="max-w-lg"
        data-testid="credit-memo-reject-modal"
      >
        <DialogHeader>
          <DialogTitle>{t('credit_memo.actionsCell.rejectModalTitle')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleRejectSubmit();
          }}
          className="space-y-4 px-6 py-4"
        >
          <div className="rounded-md border border-pw-border bg-pw-surface/40 p-3 text-xs text-pw-text-muted">
            <div>
              {t('credit_memo.actionsCell.memoLabel')}{' '}
              <span className="font-mono text-pw-text">{memoId.slice(-8)}</span>
            </div>
            {merchantLabel ? (
              <div>
                {t('credit_memo.apply.merchantLabel')} {merchantLabel}
              </div>
            ) : null}
            {typeof amountUsdCents === 'number' ? (
              <div>
                {t('credit_memo.actionsCell.amountLabel')} $
                {(Math.abs(amountUsdCents) / 100).toFixed(2)}
              </div>
            ) : null}
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-pw-text">
              {t('credit_memo.actionsCell.reasonLabel')}{' '}
              <span className="text-rose-600 dark:text-rose-300">*</span>
            </span>
            <textarea
              data-testid="credit-memo-reject-reason-input"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={5}
              minLength={REJECT_REASON_MIN}
              maxLength={REJECT_REASON_MAX}
              required
              aria-invalid={Boolean(rejectError)}
              className="w-full rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text placeholder:text-pw-text-muted focus:border-pw-primary focus:outline-none focus:ring-1 focus:ring-pw-primary"
              placeholder={t('credit_memo.actionsCell.reasonPlaceholder', {
                min: REJECT_REASON_MIN,
              })}
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span
                className={
                  reasonValid
                    ? 'text-pw-text-muted'
                    : 'text-amber-700 dark:text-amber-300'
                }
              >
                {t('credit_memo.actionsCell.charCount', {
                  count: reasonTrimLen,
                  max: REJECT_REASON_MAX,
                  min: REJECT_REASON_MIN,
                })}
              </span>
              {rejectError ? (
                <span className="text-rose-600 dark:text-rose-300" role="alert">
                  {rejectError}
                </span>
              ) : null}
            </div>
          </label>
          <p className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-2 text-xs text-amber-800 dark:text-amber-300">
            {t('credit_memo.actionsCell.rejectNotice')}
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={closeReject}
              disabled={rejecting}
            >
              {t('credit_memo.actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={rejecting || !reasonValid}
              data-testid={`credit-memo-reject-submit-${memoId}`}
            >
              {rejecting
                ? t('credit_memo.actionsCell.rejecting')
                : t('credit_memo.actionsCell.rejectSubmit')}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
    </>
  );
}

export default CreditMemoApprovalActionsCell;
